import { useEffect, useRef, useState } from 'react';
import { ViewTypes } from '..';
import { Stack, Typography, useTheme } from '@mui/material';
import { Button } from '@repo/ui';
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic';
import {
  ContactDetailsFields,
  isContactDetailsValid,
} from 'components/ContactDetailsFields';
import { GoogleLoginButton } from 'components/GoogleLoginButton/GoogleLoginButton';
import {
  clearAccessIntent,
  consumeAccessIntent,
  peekAccessIntent,
  rememberAccessIntent,
  rememberReturnPath,
} from 'utils/postAuthReturn';
import type { AccessIntent } from 'utils/postAuthReturn';
import {
  clearAccessConsent,
  writeAccessConsent,
  requestPositionForAccess,
  watchGeolocationPermission,
} from 'utils/accessConsent';
import { createApiCall } from 'services';
import { useQueryClient } from '@tanstack/react-query';
import { storeContactCard } from 'hooks/api/useContactCard';
import { useSnackbar } from 'components/Snackbar';
import { useGateRendersOwnTitle, useGateSurfaceSx } from './gatePresentation';
import { Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';

/**
 * The four ways a REQUIRED location can fail, and the four different things to say.
 *
 * Kept as module constants so the pre-flight and the prompt-declined path cannot drift
 * into two sentences for one situation, and so the copy is greppable from a bug report.
 */
export const LOCATION_FAILURE_COPY = {
  /**
   * Permission refused — either already stored as `denied`, or declined at the prompt.
   * The visitor can fix this, and browser settings is where.
   *
   * ⚠️ THE SECOND SENTENCE IS A PROMISE THE PAGE NOW KEEPS. It used to read "then try
   * again", which put the entire burden of noticing on the visitor: change the setting,
   * come back, work out that a tap is required, tap. The page watches the permission
   * (`watchGeolocationPermission`) and submits itself the moment it flips, so the
   * instruction stops at the part they actually have to do.
   *
   * No platform-specific gesture here, and that is a deliberate call rather than an
   * omission. "Tap aA in the address bar, then Website Settings, then Location" is the
   * genuinely useful instruction on iOS Safari and is WRONG everywhere else, and
   * deciding which to show needs UA sniffing that gets iPadOS, in-app browsers and
   * Android Chrome variants wrong often enough to hand some readers a set of steps that
   * do not exist on their device. A correct generic sentence plus automatic recovery
   * beats a specific sentence that is sometimes a lie.
   */
  denied: {
    title: 'Location access needed',
    text: 'Allow location for this site in your browser settings. This page continues on its own as soon as you do.',
  },
  /**
   * The EMBEDDING PAGE refuses it — an insecure (`http:`) storefront, or the brand's own
   * `Permissions-Policy: geolocation=()`. See `isGeolocationBlockedByPage`.
   *
   * This copy exists because the `denied` sentence is actively harmful here: the visitor
   * has no setting that can override a document-level policy, so sending them to browser
   * settings sends them somewhere the toggle is absent or inert, with nothing telling
   * them why. It names the cause instead and does not offer a fix they do not have.
   */
  blocked: {
    title: 'Location is blocked on this page',
    text: 'The website this certificate is shown on does not allow location access, so it cannot be turned on from your settings. The store needs to enable it.',
  },
  /**
   * PERMISSION IS FINE. The device returned no fix inside the budget, which is common
   * indoors on cellular — the ordinary place to tap a tag.
   *
   * This is the case that must never be given the `denied` sentence. Sending someone
   * whose permission is already granted to a settings screen sends them to the one place
   * they cannot fix anything, and they have no way to discover that.
   */
  unavailable: {
    title: 'Could not get your location',
    text: 'Your location permissions are set correctly. Your device just did not return a position in time. Try again, or move somewhere with a stronger signal.',
  },
  /** No geolocation API at all. Not fixable here, so name the thing that is. */
  unsupported: {
    title: 'Location not supported',
    text: 'This browser cannot share your location. Try opening this link in a different browser.',
  },
} as const;

/**
 * The shortest a location check is allowed to LOOK like it took.
 *
 * 450ms. Below ~300 a spinner that appears and vanishes reads as a flash rather than as
 * work; past ~600 an answer we already have starts to feel like an artificial wait. This
 * is not padding a slow path — the slow path is capped at 15s and needs no help. It is
 * for the INSTANT path: the stored-`denied` pre-flight answers without touching the
 * device, so without a floor the button never visibly does anything at all.
 */
const MIN_CHECK_MS = 450;

export const RequestAccessInfo = ({
  viewType,
  certificateId,
  isAccessDataCollected,
  setIsAccessDataCollected,
  collectEmail,
  collectLocation,
  initialEmail,
  companyId,
}: {
  viewType: ViewTypes;
  certificateId: string;
  isAccessDataCollected: boolean;
  setIsAccessDataCollected: (isAccessDataCollected: boolean) => void;
  /**
   * The brand's "Collect email" setting, resolved for THIS certificate down the same
   * certificate → product → company chain as `collectLocation`.
   *
   * `false` renders NO email field. The two settings are independent, so a brand can
   * require only a location — and that gate has to BE a gate with no email on it,
   * rather than an email form with a location requirement bolted underneath. This prop
   * is what makes the component cover all four combinations instead of three: the gate
   * used to be mounted on `collectEmail` alone, so "require a location, no email"
   * rendered nothing at all and the details simply opened.
   *
   * REQUIRED, with no default, for the same reason `collectLocation` is: a default
   * makes one of the four combinations the one you get by forgetting to pass it.
   *
   * ⚠️ Both flags `false` is not a state to render this in — there is nothing to
   * collect, so there is no gate. Both callers resolve that to "show the certificate"
   * before they reach here.
   */
  collectEmail: boolean;
  /**
   * The brand's "Collect location" setting, resolved for THIS certificate down the
   * certificate → product → company chain (`useCertificateAccessSettings`).
   *
   * ## VRG-591: this is now a HARD REQUIREMENT, not a request
   *
   * `true` means a real position must be acquired before the certificate's details are
   * released. Deny, decline at the prompt, or return no fix in time and the submit STOPS:
   * nothing is POSTed and the visitor stays on the gate with an instruction and a retry.
   *
   * That is a deliberate reversal. It used to race `getCurrentPosition` against a 6s cap
   * and let allow / deny / timeout all fall through to the POST with the email intact, so
   * the setting bought a prompt and nothing else — you got in either way.
   *
   * ## The consent checkbox is GONE, and its removal is the point
   *
   * A checkbox you must tick to proceed is not consent, it is a nag: it offers a choice
   * whose only real option is yes, and unticking it produced a third state ("asked, and
   * declined") that no longer exists now the setting is binary. What replaces it is a
   * plain disclosure above the button saying location is required and what it is for —
   * the same information, without pretending there is a decision to make. The actual
   * consent is the browser's own permission prompt, which is the one the visitor can
   * genuinely refuse and can revoke later.
   *
   * `false` means no position is requested, no disclosure is shown, and the browser's
   * "Allow location?" prompt never appears.
   *
   * REQUIRED, with no default. A default would make the permissive behaviour the one you
   * get by forgetting to pass it, which is the shape of the defect this closes.
   */
  collectLocation: boolean;
  /**
   * Pre-fill for the email field.
   *
   * Set when a returning visitor's STORED consent no longer satisfies the gate as it now
   * stands (`cachedAccessSatisfies` → the re-check in `CertificatePage`), which in
   * practice means the brand switched location on since they last visited. Their email is
   * not the part in question, so re-typing it is friction for a requirement they already
   * met; the ask narrows to what actually changed.
   */
  initialEmail?: string;
  /** The certificate's brand, for "Continue with Google" (see `GoogleLoginButton`). */
  companyId?: string;
  /**
   * ⚠️ THERE IS DELIBERATELY NO `description` OR `surface` PROP.
   *
   * Both existed for exactly one caller — the full-page gate at `pages/CertificateAccess`,
   * now deleted for the second time (see the note at the former `<Route>` in `App.tsx`).
   * `surface='plain'` dropped the state-card recipe because that page supplied its own
   * card; `description` overrode the sentence below with a brand-attributed form, needed
   * only because a bare page has no brand hero above it to say who is asking. The inline
   * card sits under the certificate's own photograph and title, so it does.
   *
   * They are not kept "in case". This component is the ONE implementation of the gate,
   * and a presentation prop with no caller is the seam a second presentation grows back
   * along — which is what happened both previous times (§36 / §47). The sentence below
   * still switches on `collectEmail`; that is internal, and it stays.
   */
}) => {
  // VRG-720 comparison harness — `card` (in-flow) or `sheet`. See gatePresentation.
  const gateSurfaceSx = useGateSurfaceSx();
  const gateRendersOwnTitle = useGateRendersOwnTitle();
  // Resolved here rather than via an `sx` callback: `@repo/ui`'s Input merges
  // `sx` with an object spread, so a `theme => …` form spreads to `{}` and is
  // silently dropped at runtime.
  const theme = useTheme();

  //local state
  const [accessEmail, setAccessEmail] = useState<string>(initialEmail ?? '');
  // VRG-720: the name asked for next to the email. Required whenever the email is (see
  // `isContactDetailsValid`), because a lead with an address and no name is a stranger.
  const [accessName, setAccessName] = useState<string>('');
  /*
    VRG-720 — the contact card's other three. Held here rather than inside
    `ContactDetailsFields` for the same reason name and email are: this component
    is what POSTs them, and state owned by the field component would have to be
    lifted the moment it is submitted. There is no DOM-autofill fallback for
    these (unlike name/email) because none of the three is required, so an
    autofill this component never heard about costs a blank optional field rather
    than a dead submit button.
  */
  const [accessCompany, setAccessCompany] = useState<string>('');
  const [accessTitle, setAccessTitle] = useState<string>('');
  const [accessPhone, setAccessPhone] = useState<string>('');
  // Set after a failed attempt so every invalid field shows its error, touched or not.
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Safari (esp. iOS) autofill can populate the field visually without firing a React
  // change event, so `accessEmail` state stays empty while the input's DOM value is set.
  // We read this ref on submit as a fallback so the button never acts on a stale-empty
  // state (which previously left it disabled / "unclickable").
  const emailInputRef = useRef<HTMLInputElement>(null);
  /**
   * Which of the three location failures we are showing, or `null`. The KEY is held
   * rather than a rendered string so the button can also read it — a failed attempt
   * relabels the primary action "Try again", which is what makes the retry explicit
   * without adding a second control competing with it for the same tap.
   */
  const [locationFailure, setLocationFailure] = useState<
    keyof typeof LOCATION_FAILURE_COPY | null
  >(null);
  /**
   * A re-check landed on the SAME answer as the one already on screen.
   *
   * This is the whole of "Try again does nothing". On a stored `denied`, the pre-flight
   * returns without asking the device, `setLocationFailure('denied')` writes the value
   * it already held, and React renders byte-identical output — so the tap is real, the
   * work is real, and the screen is unchanged. A control that looks inert teaches the
   * visitor it is broken, and here that reading is worse than wrong: it is the last
   * thing standing between someone holding a genuine product and its certificate.
   *
   * So a repeat answer says so out loud rather than being silent about it. Paired with
   * `MIN_CHECK_MS` below, every tap is acknowledged twice: the button spends a visible
   * moment checking, and the result names itself as a re-check.
   */
  const [repeatedCheck, setRepeatedCheck] = useState(false);
  //

  //selectors
  let { profile } = useSelector(
    ({ auth }) => ({
      profile: auth.profile,
    })
  );
  //

  //hooks
  const { openSnackbar } = useSnackbar();
  // VRG-720: a gated product releases its contact card in the access response.
  const queryClient = useQueryClient();
  //

  //queries and mutations
  const { data: certificate } = useGetCertificatePublic(certificateId);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  //

  //handlers
  /**
   * Acquire the position the gate requires, or report why it could not be had.
   *
   * Pulled out of the submit handler (VRG-720) because "Continue with Google" needs the very
   * same step BEFORE it leaves the page: the redirect that comes back has no user gesture, so
   * it cannot raise the browser's permission prompt. The tap on Google is the last gesture
   * the visitor makes, and the position is taken then and carried through sign-in.
   *
   * On failure this has ALREADY put the instruction on screen (`locationFailure`); callers
   * only need to stop. `{ ok: true, coords: null }` is the normal answer for a brand that
   * collects no location.
   */
  const acquireLocation = async (): Promise<
    | { ok: true; coords: { latitude: number; longitude: number; accuracy?: number } | null }
    | { ok: false }
  > => {
    if (!collectLocation) {
      setLocationFailure(null);
      setRepeatedCheck(false);
      return { ok: true, coords: null };
    }

    const startedAt = Date.now();
    const position = await requestPositionForAccess();

    // A FLOOR ON THE VISIBLE CHECK, not a fake delay for its own sake. The stored-
    // `denied` pre-flight settles in about a millisecond because it never reaches the
    // device, so `isSubmitting` goes true and false inside one React batch and the
    // spinner is never painted. Without this, the single most common failure — the one
    // the visitor is already stuck on — is the one where the button appears dead.
    const elapsed = Date.now() - startedAt;
    if (elapsed < MIN_CHECK_MS) {
      await new Promise(resolve => setTimeout(resolve, MIN_CHECK_MS - elapsed));
    }

    if (!position.ok) {
      // Read BEFORE the write, so "same answer as last time" is measured against what
      // is currently on screen rather than against what we are about to put there.
      setRepeatedCheck(locationFailure === position.reason);
      setLocationFailure(position.reason);
      return { ok: false };
    }

    // A position was acquired, so any instruction still showing describes a state that
    // no longer holds. Cleared here rather than at the top: blanking it up front would pull
    // the instruction the visitor is mid-way through reading off the screen for the
    // duration of the check and then put an identical one back.
    setLocationFailure(null);
    setRepeatedCheck(false);
    return { ok: true, coords: position.coords };
  };

  const handleAccessSubmit = async () => {
    // Prefer React state; fall back to the input's live DOM value for Safari autofill.
    // Only meaningful when the brand collects one — there is no field to read otherwise,
    // and the ref points at nothing.
    const email = collectEmail
      ? (accessEmail || emailInputRef.current?.value || '').trim()
      : undefined;
    const name = collectEmail
      ? (accessName || nameInputRef.current?.value || '').trim()
      : undefined;
    if (email && email !== accessEmail) setAccessEmail(email);
    if (name && name !== accessName) setAccessName(name);

    // GUARDED ON THE SETTING, not on the value. Validating an absent email on the
    // location-only gate would fail a field the visitor was never shown, leaving them
    // on a page whose only error refers to an input that is not there.
    //
    // The button is disabled until this passes, so reaching here with invalid details means
    // the auto-retry path (the permission watcher) or an autofill that landed between
    // renders. Nothing is dropped silently: every invalid field shows its error.
    if (collectEmail && !isContactDetailsValid(name ?? '', email ?? '')) {
      setShowFieldErrors(true);
      return;
    }

    // VRG-165: geolocation MUST be requested here, inside the click handler, because
    // browsers (Safari especially) silently ignore getCurrentPosition unless it's tied to
    // a user gesture — an on-mount prefetch never shows the permission prompt. So the tap
    // on this button is what triggers "Allow location?".
    //
    // VRG-591: it now BLOCKS. A failure returns early with an instruction instead of
    // falling through to the POST, because `collectLocation` is a requirement to view the
    // details, not a best-effort extra. Nothing is written on a failed attempt: no access
    // row, no cached consent — a visitor who never shared a location must not end up with
    // a stored consent saying they did.
    //
    // The position is deliberately NOT memoised across attempts. `maximumAge` already
    // serves a recent fix from the browser's own cache without a round trip, and holding
    // our own copy would let a retry "succeed" on a position acquired before the visitor
    // moved, or before they revoked the permission we are re-checking.
    //
    // ⚠️ THE FAILURE IS NOT CLEARED UP FRONT, deliberately. Blanking it here would pull
    // the instruction the visitor is mid-way through reading off the screen for the
    // duration of the check and then put an identical one back — a flicker that reads as
    // a glitch, not as progress. It is cleared on success instead, and REPLACED on
    // failure, so the block only ever changes when the answer does.
    setIsSubmitting(true);
    const located = await acquireLocation();
    if (!located.ok) {
      setIsSubmitting(false);
      return;
    }
    const coords = located.coords;

    try {
      const response = await createApiCall({
        method: 'POST',
        url: `certificates/public/${certificateId}/access`,
        data: {
          // OMITTED, not sent as `undefined`/`''`, when the brand collects none. The
          // endpoint refuses an email SUBMITTED under `collect_email: false` — that
          // refusal is the scraper control and is deliberately not relaxed — so
          // sending an empty one here would 403 the location-only gate on its own
          // submission.
          ...(email ? { email } : {}),
          // VRG-720: the name travels WITH the email and is omitted with it. Sent only when
          // there is one to send, so a location-only submission posts neither.
          ...(email && name ? { name } : {}),
          // VRG-720: and the same for the contact card's other three. Each is sent
          // independently — a card with a phone and no employer must still send the
          // phone — and all of them ride the same email gate as the name, because
          // the server will only persist them alongside one.
          ...(email && accessCompany.trim() ? { company: accessCompany.trim() } : {}),
          ...(email && accessTitle.trim() ? { title: accessTitle.trim() } : {}),
          ...(email && accessPhone.trim() ? { phone: accessPhone.trim() } : {}),
          // The gate disclosed that location is required and the visitor proceeded
          // through the browser's own permission prompt, so `consent` tracks the brand's
          // setting exactly. There is no longer a narrower visitor-level answer to carry:
          // the checkbox that used to supply one offered a choice with one real option.
          consent: collectLocation,
          location: coords
            ? { latitude: coords.latitude, longitude: coords.longitude }
            : undefined,
        },
      });

      if (response?.status && ![200, 201].includes(response.status)) {
        throw new Error(`status ${response.status}`);
      }

      // VRG-720: the access response carries the product's contact card when this
      // submission satisfies the gate; the certificate page renders it from this cache entry.
      storeContactCard(queryClient, certificateId, response);

      // Remember the decision for this ORIGIN so the next certificate on this brand's
      // domain does not re-ask. Only the email and the location CONSENT are kept — never
      // the position, which is re-acquired per access (see accessConsent.ts).
      // What is cached is what was actually GIVEN. This is only reached when a position
      // was genuinely acquired (or none was required), so `collectLocation` here means
      // "a location was shared", which is exactly what `cachedAccessSatisfies` reads it
      // back as. Caching it while the brand collects no location would store a consent
      // the visitor was never asked for, and would then wave them past the gate if the
      // brand later switched location on.
      writeAccessConsent(email, collectLocation);
      setIsAccessDataCollected(true);
    } catch (error) {
      console.error('Error logging certificate access:', error);
      openSnackbar({
        severity: 'error',
        title: 'Unable to access certificate',
        text: 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * "Continue with Google" -- runs on the tap, BEFORE the redirect (VRG-720).
   *
   * Three things have to be true when the visitor comes back from Google, and all three are
   * arranged here because nothing after the redirect has a user gesture:
   *
   *  1. THE POSITION, if the gate requires one. Acquired now (same step, same failure
   *     instructions as the email form) and carried in the intent. A failure stops the
   *     redirect and shows the usual instruction, so Google is not a way around a required
   *     location.
   *  2. WHERE TO COME BACK TO. `AuthCallback` used to send everyone to `/certificates`.
   *  3. WHAT THEY WERE DOING. The intent says "submit the access request for THIS
   *     certificate"; the effect below reads it once a profile exists.
   *
   * The stored consent is cleared: it says "this origin already has an email", which would
   * make the page skip the gate on return and never mount the code that submits the intent.
   * Choosing Google is choosing a different identity, so the old cached one is stale anyway.
   *
   * Returns `false` to cancel the redirect (see `GoogleLoginButton.onBeforeRedirect`).
   */
  const handleGoogleContinue = async (): Promise<boolean> => {
    setIsSubmitting(true);
    const located = await acquireLocation();
    if (!located.ok) {
      setIsSubmitting(false);
      return false;
    }
    clearAccessConsent();
    rememberAccessIntent({
      certificateId,
      locationConsent: collectLocation,
      location: located.coords
        ? { latitude: located.coords.latitude, longitude: located.coords.longitude }
        : undefined,
    });
    // The certificate's own path, never `window.location`: a signed-out visitor's 401s
    // run `logOut()` in services/index.ts, which pushes `/sign-in` into the address bar
    // without re-rendering, so the location can read `/sign-in` while this page is up.
    rememberReturnPath(`/certificates/${certificateId}`);
    // Left submitting: the page is about to navigate away.
    return true;
  };

  /**
   * The other half of Google: submit the access request on behalf of the signed-in profile.
   *
   * Best effort by design. The page already treats any signed-in viewer as past the gate
   * (see the profile effect below), so a failed write here costs the brand one analytics row
   * and never keeps a person out of a certificate -- which is also why it is silent where
   * the email form shows an error.
   */
  const submitSignedInAccess = async (
    intent: AccessIntent,
    signedIn: { first_name?: string; last_name?: string; email?: string },
  ) => {
    const email = collectEmail ? signedIn.email?.trim() : undefined;
    const name = `${signedIn.first_name ?? ''} ${signedIn.last_name ?? ''}`.trim();
    const locationConsent = collectLocation && intent.locationConsent;
    try {
      const response = await createApiCall({
        method: 'POST',
        url: `certificates/public/${certificateId}/access`,
        data: {
          ...(email ? { email } : {}),
          ...(email && name ? { name } : {}),
          consent: locationConsent,
          location: locationConsent ? intent.location : undefined,
        },
      });
      storeContactCard(queryClient, certificateId, response);
      writeAccessConsent(email, locationConsent);
    } catch (error) {
      console.warn('Could not log certificate access after Google sign-in:', error);
    }
  };
  //

  //Effects
  // NOTE: geolocation is intentionally NOT prefetched on mount — browsers ignore a
  // gesture-less getCurrentPosition (no prompt shows), so the request lives in the
  // submit handler where the tap provides the required user gesture.
  //
  // VRG-720: a Google sign-in started from THIS form leaves an intent behind. Consumed
  // (read + removed) the moment a profile exists, so a re-render, a StrictMode double
  // effect or a second mount cannot POST the request twice -- same idea as
  // `ClaimOwnershipSignUp`'s `hasAutoClaimed`, with the storage read as the real guard and
  // the ref as the belt. It runs BEFORE the `setIsAccessDataCollected(true)` below because
  // that unmounts this component; the request is already in flight by then, and an
  // unmounted component's pending promise is harmless.
  const autoSubmitStarted = useRef(false);
  const [awaitingSignIn, setAwaitingSignIn] = useState<boolean>(
    () => peekAccessIntent(certificateId) !== null,
  );

  useEffect(() => {
    if (profile) {
      if (!autoSubmitStarted.current) {
        const intent = consumeAccessIntent(certificateId);
        if (intent) {
          autoSubmitStarted.current = true;
          void submitSignedInAccess(intent, profile);
        }
      }
      setAccessEmail(profile?.email);
      setIsAccessDataCollected(true);
    }
  }, [profile]);

  // The profile arrives a beat after the redirect lands. Until it does the page would show
  // the empty form, which reads as "that didn't work", so a pending intent shows a short
  // "finishing" state instead. Bounded: if the profile never comes (the sign-in failed),
  // the intent is dropped and the form returns, so nobody is stuck behind a spinner.
  useEffect(() => {
    if (!awaitingSignIn) return;
    if (profile) {
      setAwaitingSignIn(false);
      return;
    }
    const timeout = window.setTimeout(() => {
      clearAccessIntent();
      setAwaitingSignIn(false);
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [awaitingSignIn, profile]);

  /**
   * RECOVER FROM `denied` WITHOUT A TAP.
   *
   * `denied` is the only failure with no path through the page: the pre-flight answers
   * from the browser's permission store, so no number of retries can change the answer
   * and the only thing that can lives in browser settings. The visitor leaves, flips the
   * toggle, comes back — and, before this, arrived at a page that still said "denied"
   * and gave them no reason to think a tap would now behave differently.
   *
   * `PermissionStatus`'s `change` event closes that. The page sees the flip and finishes
   * the job the visitor already asked for.
   *
   * ## Why it AUTO-SUBMITS rather than just re-enabling
   *
   * Re-enabling would leave the same discovery problem one step smaller: they would
   * still have to notice something had changed and act on it. Their intent is not in
   * doubt — they tapped the button, and (when an email is collected) they already typed
   * one and passed validation, which is the only way `locationFailure` gets set at all.
   * Consuming a grant they just made is finishing that action, not starting a new one.
   *
   * ## Why the auto-submit is safe without a user gesture
   *
   * The gesture requirement is for showing the PROMPT. This only fires on `granted`,
   * where no prompt is needed and `getCurrentPosition` resolves from the existing grant
   * — the same assumption `getLocationIfAlreadyPermitted` already makes from an effect
   * elsewhere in this flow. It deliberately does NOT arm on any other state, so we never
   * try to raise a prompt from outside a tap.
   *
   * Armed ONLY while a `denied` failure is on screen. `unavailable` has working
   * permission already, and `blocked` is a page policy the visitor cannot flip, so
   * neither has anything to watch for.
   */
  const submitRef = useRef(handleAccessSubmit);
  submitRef.current = handleAccessSubmit;
  const isSubmittingRef = useRef(isSubmitting);
  isSubmittingRef.current = isSubmitting;

  useEffect(() => {
    if (!collectLocation || locationFailure !== 'denied') return;

    return watchGeolocationPermission(state => {
      // A second flip landing while the first is in flight would double-POST.
      if (state !== 'granted' || isSubmittingRef.current) return;
      void submitRef.current();
    });
  }, [collectLocation, locationFailure]);

  const contactValid = isContactDetailsValid(accessName, accessEmail);

  // Back from Google, profile not loaded yet (see the effect above). Same card, same
  // footprint as the form it replaces, so nothing shifts when the page resolves.
  if (awaitingSignIn && !profile) {
    return (
      <Stack spacing={1.5} sx={gateSurfaceSx}>
        {/* Suppressed in a sheet: DrawerPopup's header already carries this
              title, and rendering it twice is the stacked-duplicate-heading
              defect ScanTag records from the gift flow. */}
        {gateRendersOwnTitle && (
        <Typography variant='h6' sx={{ fontSize: 20, fontWeight: 500 }}>
          Access certificate
        </Typography>
        )}
        <Button
          variant='primary'
          className='w-full h-10 text-center'
          sx={{ paddingX: 2 }}
          disabled
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <Loader2 className='w-5 h-5 animate-spin' />
            <span>Finishing sign in</span>
          </Stack>
        </Button>
      </Stack>
    );
  }

  return (
    /* THE SHARED STATE-CARD SURFACE, not a fourth hand-rolled card.
       This is a state card in exactly the sense `stateCardSurface.ts` defines —
       something that sits ON the certificate rather than being part of it, in
       the same family as the pending-transfer card and the lost/stolen callout —
       so it takes that file's recipe (white + 1px hairline + `elevation.card`,
       12px radius) instead of its own Tailwind approximation of it.

       It had shipped as `border-[1px] border-solid border-border rounded-md p-4`:
       the right border colour by accident, a 6px radius where the family uses 12,
       a TRANSPARENT fill, and no shadow at all — so on a page whose surface is
       already near-white it had almost no edge, which is the very defect
       `stateCardSurface`'s notes record fixing on the other two cards.

       ⚠️ THE TAILWIND CLASSES HAD TO GO, not merely be supplemented. This app sets
       `important: '#vera-shadow-root'` in `tailwind.config.js`, so every utility is
       emitted at ID specificity (1,1,0) and beats any emotion `sx` rule (0,1,0).
       Leaving `rounded-md p-4` alongside the `sx` would have silently kept the 6px
       radius — the same trap `stateCardTextSx` documents for `text-gray-500`. */
    /* GAP 12, NOT 16 (§29's 12px button-group value, on the 8px base `apps/app`
       actually uses — so `1.5`, not `3`; copying the platform's factor here would
       render 24). The card is the page's only action and it has to clear the fold on
       a phone, and at 16 it did not: measured at 375x812 the card ran to y=759 with a
       61px footer under it, i.e. 837 of an 812 viewport, so the certificate could not
       be shown without a scroll no matter what the region below it did.
       Four elements at 12 buys back 16px of that. The other 25 came off the input's
       faked 4px label margin, the placeholder's 24px top padding, the column's 16px
       bottom padding and the footer's 12px of extra top clearance — five values that
       were each independently wrong, rather than one arbitrary compression applied
       until the number fitted. Measured after: 812 of 812, no scroll. */
    <Stack spacing={1.5} sx={gateSurfaceSx}>
      <Stack spacing={1}>
        {/* Suppressed in a sheet: DrawerPopup's header already carries this
              title, and rendering it twice is the stacked-duplicate-heading
              defect ScanTag records from the gift flow. */}
        {gateRendersOwnTitle && (
        <Typography variant='h6' sx={{ fontSize: 20, fontWeight: 500 }}>
          Access certificate
        </Typography>
        )}
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          {/*
            THE DESCRIPTION STATES THE REQUIREMENT; the disclosure below states the
            RETENTION. Each fact is said exactly once, and this half is the requirement.

            It names WHAT IS BEING ASKED FOR, so it has to follow the setting. "Enter
            your email" on a gate with no email field describes a form that is not
            there — and on the location-only gate this sentence and the button are the
            entire instruction, so getting it wrong leaves the visitor with no statement
            anywhere of what the button will do.

            Unattributed on purpose. The brand's own photograph and product name sit
            directly above this card, so "{brand} requests your email" would name a
            party the page has already named. The full-page gate needed the attribution
            precisely because it had no hero to supply it; that page is gone.
          */}
          {collectEmail
            ? 'Share your name and email to view this certificate.'
            : 'Share your location to view this certificate.'}
        </Typography>
      </Stack>

      {collectEmail && (
        /* THE NAME + EMAIL PAIR is its own component (VRG-720) so slice 2's claim drawer
           reuses it. Everything the old inline email field documented about the shared
           `Input` (no `mt`, the 16px-vs-8px label gap in apps/app, autofill via
           `inputProps`) moved with it. */
        <ContactDetailsFields
          name={accessName}
          email={accessEmail}
          onNameChange={setAccessName}
          onEmailChange={setAccessEmail}
          nameInputRef={nameInputRef}
          emailInputRef={emailInputRef}
          showErrors={showFieldErrors}
          company={accessCompany}
          title={accessTitle}
          phone={accessPhone}
          onCompanyChange={setAccessCompany}
          onTitleChange={setAccessTitle}
          onPhoneChange={setAccessPhone}
          /* CONTINUE WITH GOOGLE (VRG-720), now INSIDE the fields component rather
             than after the submit button.

             It is a one-tap way to hand over a verified name + email, so it belongs
             with the other one-tap paths, above the "or enter manually" divider.
             Below the submit button it was on the wrong side of the only boundary
             on the surface: the page read as "type and submit, OR use Google",
             while "Share contact card" sat above with nothing tying it to either —
             three ways to do one thing, grouped as two unrelated halves.

             Google ONLY -- Sign in with Apple is deliberately off in this app (see
             `SocialLoginButtons` for why), and a visitor on iPhone has autofill
             instead. Only beside the email form: a gate that collects no email has
             nothing for it to fill, and `collectEmail` already gates this whole
             block. */
          quickActions={
            <GoogleLoginButton
              label='Continue with Google'
              className='w-full h-10'
              disabled={isSubmitting}
              companyId={companyId}
              onBeforeRedirect={handleGoogleContinue}
            />
          }
        />
      )}

      {/* THE DISCLOSURE THAT REPLACED THE CONSENT CHECKBOX.

          A statement, not a control, because there is no longer a decision here: the
          setting is binary and location is required, so a checkbox would have offered a
          choice with one real option and made refusing it look available. It still carries
          both things the checkbox carried — that location is collected, and that it is a
          condition of viewing — and it says the second one out loud, which the checkbox
          only implied.

          HIDDEN, not disabled, when the brand collects no location. §27's
          disable-with-a-tooltip rule is for an action the viewer could gain access to;
          this is not that, and a greyed-out location notice would advertise a collection
          the brand deliberately switched off, making the page read as more invasive than
          the setting made it.

          IT SPLITS ON `collectEmail`, and the reason is that it is not always carrying the
          same load. Alongside an email field the description above is about the EMAIL, so
          the location requirement is stated nowhere else and this sentence has to say both
          halves: that a location is required, and what becomes of it.

          On the location-only gate the description already says "Share your location to
          view this certificate", so the first half would be the second time in three lines
          the page says location is required — which reads as insistence rather than
          disclosure, and buries the half the visitor has not yet been told. So it drops to
          the part the description cannot carry: that the position is RECORDED, not merely
          checked. Between them the two lines still state the requirement, the collection
          and the retention, in that order and each once. */}
      {collectLocation && (
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          {collectEmail
            ? 'Your location is required to view this certificate. It is recorded with this scan.'
            : 'Your location is recorded with this scan.'}
        </Typography>
      )}

      {/* ABOVE the button, deliberately. This block explains why the last attempt failed
          and what to do about it, and the button immediately below it is the retry — so
          the reading order is problem, instruction, action. It sat below the button
          before, where the instruction arrived after the only control that could act on
          it. */}
      {locationFailure && (
        <Stack spacing={0.5}>
          <Typography
            variant='body2'
            sx={{ fontWeight: 500, color: theme.palette.error.main }}
          >
            {LOCATION_FAILURE_COPY[locationFailure].title}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            {LOCATION_FAILURE_COPY[locationFailure].text}
          </Typography>
          {/* THE TAP'S RECEIPT. Only rendered when a re-check produced the answer
              already on screen — the one case where the page would otherwise be
              byte-identical before and after a press, which is indistinguishable from
              a broken button. It says what happened ("we did look") and what did not
              ("nothing changed"), so the visitor learns the setting is still where they
              left it rather than that the control is dead. */}
          {repeatedCheck && (
            <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              Checked again just now. Nothing has changed yet.
            </Typography>
          )}
        </Stack>
      )}

      <Button
        variant='primary'
        /* NO `mt-4`. Tailwind here is emitted at ID specificity (see the card note
           above), so `mt-4` was overriding the Stack's own gap and pinning this one
           child to 16px while its siblings moved to 12 — the button would have been
           the only element in the card at the old spacing, which is worse than either
           value applied consistently. The gap is the Stack's to set. */
        className='w-full h-10 hover:bg-primary/90 text-center'
        sx={{ paddingX: 2 }}
        onClick={handleAccessSubmit}
        // Disabled while submitting, and (VRG-720, working-conventions section 54) until
        // the name and email are valid -- a Continue that cannot succeed should not look
        // like it can. This used to stay live on an empty form because Safari autofill can
        // fill a field without telling React, leaving state empty and the button dead;
        // `ContactDetailsFields` now re-reads the DOM on an interval and on `change`, so the
        // state this reads is the state the visitor can see. The location-only gate has no
        // fields, so it is never disabled by this.
        //
        // ⚠️ It is never disabled by a location failure, and that is the whole retry
        // mechanism. A hard gate whose failure state has no live control is a dead end,
        // and a dead end on this page means a customer holding a real product cannot
        // reach its certificate at all. Even `unsupported` leaves it live: pressing it
        // costs one re-check and the alternative is a page with nothing to press.
        disabled={isSubmitting || (collectEmail && !contactValid)}
      >
        {isSubmitting ? (
          /* NAMED, not a bare spinner. A retry that lands back on the same instruction
             leaves the result text unchanged, so the in-flight state is the only place
             the page can say "your tap did something" — and an unlabelled spinner on a
             button whose answer never changes is a weak thing to hang that on. The verb
             also sets the expectation the answer might be the same one again. */
          <Stack direction='row' spacing={1} alignItems='center'>
            <Loader2 className='w-5 h-5 animate-spin' />
            <span>{locationFailure ? 'Checking again' : 'Checking'}</span>
          </Stack>
        ) : locationFailure ? (
          // The retry, named. Re-labelling the primary action is what makes it explicit
          // without introducing a second button beside it competing for the same tap —
          // and it re-runs the whole submit, including a fresh permission pre-flight, so
          // a visitor who has just fixed their browser setting gets through on this press
          // rather than having to reload the page.
          'Try again'
        ) : (
          'Access certificate'
        )}
      </Button>

    </Stack>
  );
};
